import type { TenantKind } from "stores/ComponentStores/OrgSelectorComponentStore";

/**
 * `Classifiers.CreatedBy` marker written by the classifier-library copy routines
 * (see AddCoreClassifierToTenant / AddClassifiersFromCoreGroupToTenant in
 * ClassifierController).
 */
export const LIBRARY_AUTHOR = "AuthenticxClassifierLibrary";

export interface ClassifierEditabilityInput {
    /** `createdBy` of the classifier the builder is offering Edit for. */
    createdBy?: string;
    tenant: TenantKind;
    /** Holds the "Builder" edit permission. */
    canEditBuilder: boolean;
    /** Holds the "Classifier Library Edit" permission. */
    canEditLibrary: boolean;
}

/**
 * Whether the builder should offer Edit for a classifier.
 *
 * Library-authored RBCs are editable on the tenant they were copied to
 * (AUT-10752). The library records themselves live on the Authcx tenant and
 * still require "Classifier Library Edit".
 *
 * While the tenant is unresolved no library record offers Edit — including to
 * callers who hold "Classifier Library Edit" — so the button appears once the
 * organization is known instead of popping in on incomplete information.
 *
 * Ordinary (non-library) classifiers do not consult the tenant at all: they
 * depend on "Builder" alone, exactly as they did before this rule existed.
 */
export function canEditClassifier({
    createdBy,
    tenant,
    canEditBuilder,
    canEditLibrary,
}: ClassifierEditabilityInput): boolean {
    if (!canEditBuilder) return false;
    if (createdBy !== LIBRARY_AUTHOR) return true;
    // Library records show nothing until the organization resolves, even to a
    // caller holding "Classifier Library Edit" — the button appears once we
    // know which tenant we are on, rather than popping in and out.
    if (tenant === "unresolved") return false;
    return tenant === "tenant" || canEditLibrary;
}
